import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import './QuizGame.css';
import './KBCMultiplex.css';

const KBCHostContestant = ({ 
  gameSession, 
  userRole, 
  userName, 
  userClass, 
  onExit 
}) => {
  const [socket, setSocket] = useState(null);
  const [gameState, setGameState] = useState('waiting');
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [contestantScore, setContestantScore] = useState(0);
  const [isHost, setIsHost] = useState(false);
  const [contestantAnswer, setContestantAnswer] = useState('');
  const [contestantName, setContestantName] = useState('');

  const prizeAmounts = [100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 500000, 1000000];

  // Audio management
  const [currentAudio, setCurrentAudio] = useState(null);
  const [timerAudio, setTimerAudio] = useState(null);

  const playAudio = (audioFile) => {
    try {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      
      const audio = new Audio(`https://therisingschool.com/Learning%20Games/MP3/${audioFile}`);
      audio.volume = 0.5;
      setCurrentAudio(audio);
      
      audio.play().catch(error => console.log('Audio play failed:', error));
      audio.onended = () => setCurrentAudio(null);
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  };

  const startTimerAudio = () => {
    try {
      if (timerAudio) {
        timerAudio.pause();
        timerAudio.currentTime = 0;
      }
      
      const audio = new Audio('https://therisingschool.com/Learning%20Games/MP3/Timer.mp3');
      audio.volume = 0.3;
      audio.loop = true;
      setTimerAudio(audio);
      
      audio.play().catch(error => console.log('Timer audio play failed:', error));
    } catch (error) {
      console.log('Timer audio not supported:', error);
    }
  };

  const stopTimerAudio = () => {
    if (timerAudio) {
      timerAudio.pause();
      timerAudio.currentTime = 0;
      setTimerAudio(null);
    }
  };

  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    stopTimerAudio();
  };

  // Initialize socket connection
  useEffect(() => {
    const gameId = gameSession?.id || gameSession?.gameSession?.id;
    if (!gameId) return;
    
    const newSocket = io('http://localhost:5000');
    setSocket(newSocket);

    // Determine if current user is host (challenge sender)
    const challenger = gameSession?.challenger || gameSession?.gameSession?.challenger;
    const isUserHost = challenger === userName;
    setIsHost(isUserHost);

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      newSocket.emit('join-kbc-host-game', { gameId, userName, isHost: isUserHost });
    });

    // Host receives contestant's answer
    newSocket.on('contestant-answer', (data) => {
      if (isUserHost) {
        setContestantAnswer(data.answer);
        setShowResult(true);
      }
    });

    // Contestant receives next question from host
    newSocket.on('next-question', (data) => {
      if (!isUserHost) {
        setCurrentQuestion(data.questionIndex);
        setSelectedAnswer('');
        setShowResult(false);
        playAudio('Question.mp3');
        setTimeout(() => startTimerAudio(), 1000);
      }
    });

    // Both receive score updates
    newSocket.on('score-update', (data) => {
      setContestantScore(data.score);
    });

    // Game end
    newSocket.on('game-ended', () => {
      setGameState('finished');
      stopAudio();
    });

    return () => {
      if (gameId) {
        newSocket.emit('leave-kbc-host-game', gameId);
      }
      newSocket.disconnect();
    };
  }, [gameSession, userName]);

  // Load questions and setup game
  useEffect(() => {
    const questions = gameSession?.questions || gameSession?.gameSession?.questions;
    const players = gameSession?.players || gameSession?.gameSession?.players || [];
    
    if (questions && questions.length >= 12) {
      setQuestions(questions.slice(0, 12));
      setGameState('playing');
      
      // Set contestant name
      const contestant = players.find(p => p.name !== userName);
      setContestantName(contestant?.name || 'Contestant');
      
      // Host starts the first question
      if (isHost) {
        setTimeout(() => {
          const gameId = gameSession?.id || gameSession?.gameSession?.id;
          socket?.emit('start-question', { gameId, questionIndex: 0 });
        }, 1000);
      }
    }
  }, [gameSession, isHost, socket]);

  // Contestant submits answer
  const submitAnswer = () => {
    if (isHost) return; // Host doesn't answer
    
    stopTimerAudio();
    const gameId = gameSession?.id || gameSession?.gameSession?.id;
    socket?.emit('submit-answer', { 
      gameId, 
      answer: selectedAnswer, 
      questionIndex: currentQuestion 
    });
  };

  // Host moves to next question
  const nextQuestion = () => {
    if (!isHost) return;
    
    const correct = contestantAnswer === questions[currentQuestion].correctAnswer;
    const points = correct ? prizeAmounts[currentQuestion] : 0;
    const newScore = contestantScore + points;
    
    setContestantScore(newScore);
    
    const gameId = gameSession?.id || gameSession?.gameSession?.id;
    socket?.emit('update-score', { gameId, score: newScore, isCorrect: correct });
    
    if (currentQuestion < questions.length - 1) {
      const nextIndex = currentQuestion + 1;
      setCurrentQuestion(nextIndex);
      setContestantAnswer('');
      setShowResult(false);
      
      socket?.emit('start-question', { gameId, questionIndex: nextIndex });
    } else {
      socket?.emit('end-game', { gameId, finalScore: newScore });
    }
  };

  if (gameState === 'waiting') {
    return (
      <div className="quiz-game kbc-multiplex-mode">
        <div className="prize-ladder-container">
          <h2>🎯 KBC Host-Contestant Mode</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div>Setting up the game...</div>
            <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              Host: {gameSession?.challenger || gameSession?.gameSession?.challenger}<br/>
              Contestant: {contestantName}
            </div>
          </div>
          <button onClick={() => { stopAudio(); onExit(); }} className="quit-btn">Exit Game</button>
        </div>
      </div>
    );
  }

  if (gameState === 'finished') {
    return (
      <div className="quiz-game kbc-multiplex-mode">
        <div className="prize-ladder-container">
          <h2>🏆 Game Over!</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <h3>{isHost ? 'Game Hosted Successfully!' : 'Your Performance:'}</h3>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981', margin: '20px 0' }}>
              Final Score: {contestantScore.toLocaleString()} Points
            </div>
            <div style={{ fontSize: '16px', color: '#666' }}>
              Questions Completed: {questions.length}/12
            </div>
          </div>
          <button onClick={() => { stopAudio(); onExit(); }} className="continue-btn">Back to Games</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  if (!currentQ) return <div>Loading...</div>;

  // HOST VIEW
  if (isHost) {
    return (
      <div className="quiz-game kbc-multiplex-mode">
        <div className="game-header">
          <div className="points-display">
            <div className="coin-animation">
              🏆 Host Dashboard
            </div>
          </div>
          
          <div className="timer-circle multiplex-timer-circle">
            <div className="timer-inner multiplex-timer-inner">
              <span>HOST</span>
            </div>
          </div>
          
          <div className="question-counter">
            {currentQuestion + 1}/12
          </div>
        </div>

        {/* Contestant Info */}
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: 'rgba(0,0,0,0.9)',
          color: 'white',
          padding: '15px',
          borderRadius: '8px',
          border: '2px solid #10b981',
          zIndex: 1000
        }}>
          <div style={{ fontWeight: 'bold', color: '#10b981', marginBottom: '8px' }}>
            🎯 CONTESTANT
          </div>
          <div>👤 {contestantName}</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#10b981' }}>
            💰 {contestantScore.toLocaleString()}
          </div>
        </div>

        <div className="question-section">
          <div className="question-box multiplex-question-box">
            <div className="multiplex-mode-indicator">
              <span className="multiplex-mode-badge">🎯 HOST CONTROL</span>
              <span className="multiplex-custom-badge">⚡ YOU CONTROL THE GAME</span>
            </div>
            <h3>{currentQ.question}</h3>
          </div>
          
          <div className="options-grid">
            {currentQ.options.map((option, index) => (
              <button
                key={index}
                className={`option-btn ${
                  showResult ? (option === currentQ.correctAnswer ? 'correct' : 
                  contestantAnswer === option ? 'wrong' : '') : ''
                }`}
                disabled={true}
              >
                <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                <span className="option-text">{option}</span>
              </button>
            ))}
          </div>

          {showResult && (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              background: contestantAnswer === currentQ.correctAnswer ? '#dcfce7' : '#fef2f2',
              border: `2px solid ${contestantAnswer === currentQ.correctAnswer ? '#16a34a' : '#dc2626'}`,
              borderRadius: '8px',
              margin: '20px 0'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                Contestant answered: {contestantAnswer}
              </div>
              <div style={{ fontSize: '16px', color: contestantAnswer === currentQ.correctAnswer ? '#16a34a' : '#dc2626' }}>
                {contestantAnswer === currentQ.correctAnswer ? '✅ CORRECT!' : '❌ WRONG!'}
              </div>
              <div style={{ fontSize: '14px', marginTop: '10px' }}>
                Correct Answer: {currentQ.correctAnswer}
              </div>
            </div>
          )}
        </div>

        <div className="game-controls">
          <div className="action-buttons">
            {showResult ? (
              <button 
                className="submit-btn"
                onClick={nextQuestion}
              >
                {currentQuestion < questions.length - 1 ? 'Next Question' : 'End Game'}
              </button>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Waiting for contestant to answer...
              </div>
            )}
            <button className="quit-btn" onClick={() => { stopAudio(); onExit(); }}>End Game</button>
          </div>
        </div>
      </div>
    );
  }

  // CONTESTANT VIEW
  return (
    <div className="quiz-game kbc-multiplex-mode">
      <div className="game-header">
        <div className="points-display">
          <div className="coin-animation">
            🪙 {contestantScore} Points
          </div>
        </div>
        
        <div className="timer-circle multiplex-timer-circle">
          <div className="timer-inner multiplex-timer-inner">
            <span>∞</span>
          </div>
        </div>
        
        <div className="question-counter">
          {currentQuestion + 1}/12
        </div>
      </div>

      <div className="question-section">
        <div className="question-box multiplex-question-box">
          <div className="multiplex-mode-indicator">
            <span className="multiplex-mode-badge">🎯 CONTESTANT</span>
            <span className="multiplex-custom-badge">⚡ NO TIME LIMIT</span>
          </div>
          <h3>{currentQ.question}</h3>
        </div>
        
        <div className="options-grid">
          {currentQ.options.map((option, index) => (
            <button
              key={index}
              className={`option-btn ${selectedAnswer === option ? 'selected' : ''}`}
              onClick={() => setSelectedAnswer(option)}
              disabled={showResult}
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              <span className="option-text">{option}</span>
            </button>
          ))}
        </div>

        {showResult && (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            background: '#f0f9ff',
            border: '2px solid #0ea5e9',
            borderRadius: '8px',
            margin: '20px 0'
          }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0369a1' }}>
              Answer submitted! Waiting for host...
            </div>
          </div>
        )}
      </div>

      <div className="game-controls">
        <div className="action-buttons">
          <button 
            className="submit-btn"
            onClick={submitAnswer}
            disabled={!selectedAnswer || showResult}
          >
            Final Answer
          </button>
          <button className="quit-btn" onClick={() => { stopAudio(); onExit(); }}>Quit Game</button>
        </div>
      </div>
    </div>
  );
};

export default KBCHostContestant;